// Registry of MUI icons available for admin tiles, plus the default tile
// set used when the Firestore doc settings/adminTiles is empty.
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PaymentIcon from '@mui/icons-material/Payment';
import BarChartIcon from '@mui/icons-material/BarChart';
import UploadIcon from '@mui/icons-material/Upload';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import SettingsIcon from '@mui/icons-material/Settings';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PaletteIcon from '@mui/icons-material/Palette';
import SchoolIcon from '@mui/icons-material/School';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import LinkIcon from '@mui/icons-material/Link';
import GridViewIcon from '@mui/icons-material/GridView';
import EmailIcon from '@mui/icons-material/Email';
import PeopleIcon from '@mui/icons-material/People';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BugReportIcon from '@mui/icons-material/BugReport';

export const ADMIN_TILE_ICONS = {
  DashboardCustomize: DashboardCustomizeIcon,
  MenuBook: MenuBookIcon,
  Payment: PaymentIcon,
  BarChart: BarChartIcon,
  Upload: UploadIcon,
  Notifications: NotificationsIcon,
  ManageAccounts: ManageAccountsIcon,
  Settings: SettingsIcon,
  EmojiEvents: EmojiEventsIcon,
  Palette: PaletteIcon,
  School: SchoolIcon,
  SportsVolleyball: SportsVolleyballIcon,
  CardMembership: CardMembershipIcon,
  Description: DescriptionIcon,
  Visibility: VisibilityIcon,
  MergeType: MergeTypeIcon,
  Link: LinkIcon,
  GridView: GridViewIcon,
  Email: EmailIcon,
  People: PeopleIcon,
  GroupAdd: GroupAddIcon,
  EventNote: EventNoteIcon,
  Assignment: AssignmentIcon,
  AccountBalance: AccountBalanceIcon,
  BugReport: BugReportIcon,
};

export function getAdminTileIcon(name) {
  return ADMIN_TILE_ICONS[name] || ManageAccountsIcon;
}

// Color schemes used for admin tiles. Admins pick a scheme rather than
// raw hex pairs so the dashboard stays visually consistent.
export const ADMIN_TILE_COLOR_SCHEMES = {
  blue:     { color: '#DEEAF1', iconColor: '#2E6DA4', label: 'Blue'      },
  green:    { color: '#E9F7EF', iconColor: '#27AE60', label: 'Green'     },
  yellow:   { color: '#FEF9E7', iconColor: '#E67E22', label: 'Yellow'    },
  purple:   { color: '#F5EEF8', iconColor: '#8E44AD', label: 'Purple'    },
  red:      { color: '#FDEDEC', iconColor: '#C0392B', label: 'Red'       },
  navy:     { color: '#DEEAF1', iconColor: '#1E3A5F', label: 'Navy'      },
  darkBlue: { color: '#EAF4FB', iconColor: '#1A5276', label: 'Dark Blue' },
  gold:     { color: '#FEF9E7', iconColor: '#C9A84C', label: 'Gold'      },
  olive:    { color: '#FEF9E7', iconColor: '#B7950B', label: 'Olive'     },
  forest:   { color: '#E9F7EF', iconColor: '#1E6B3C', label: 'Forest'    },
};

export function getAdminTileColors(schemeKey) {
  return ADMIN_TILE_COLOR_SCHEMES[schemeKey] || ADMIN_TILE_COLOR_SCHEMES.blue;
}

// Default admin tiles — mirrors what was previously hardcoded in
// AdminDashboard.js, with icons + colors stored as string keys.
export const DEFAULT_ADMIN_TILES = [
  { key: 'dashboard-tiles',    label: 'Dashboard Tiles',         desc: 'Customize the home screen tiles',                        icon: 'DashboardCustomize', path: '/admin/dashboard-tiles',   colorScheme: 'blue',     enabled: true },
  { key: 'menu-items',         label: 'Menu Items',              desc: 'Manage the sidebar menu and role visibility',            icon: 'MenuBook',           path: '/admin/menu-items',        colorScheme: 'blue',     enabled: true },
  { key: 'admin-tiles',        label: 'Admin Tiles',             desc: 'Manage these admin tiles',                               icon: 'GridView',           path: '/admin/admin-tiles',       colorScheme: 'navy',     enabled: true },
  { key: 'training-plans',     label: 'Training Plans',          desc: 'Manage session plans and pricing',                       icon: 'Payment',            path: '/plans',                   colorScheme: 'green',    enabled: true },
  { key: 'reports',            label: 'Reports',                 desc: 'Athletes, financials, attendance, logs',                 icon: 'BarChart',           path: '/reports',                 colorScheme: 'yellow',   enabled: true },
  { key: 'import',             label: 'Import',                  desc: 'Bulk import from PushPress CSV',                         icon: 'Upload',             path: '/import',                  colorScheme: 'purple',   enabled: true },
  { key: 'notifications',      label: 'Notifications',           desc: 'Send push notifications to users',                       icon: 'Notifications',      path: '/notifications',           colorScheme: 'red',      enabled: true },
  { key: 'tournament-admin',   label: 'Tournament Admin',        desc: 'Manage tournaments and registrations',                   icon: 'EmojiEvents',        path: '/tournament-admin',        colorScheme: 'navy',     enabled: true },
  { key: 'branding',           label: 'Branding',                desc: 'Club name, colors, logo, favicon',                       icon: 'Palette',            path: '/admin/branding',          colorScheme: 'gold',     enabled: true },
  { key: 'schools',            label: 'Schools',                 desc: 'Season schedule, rental contracts, registration list',   icon: 'School',             path: '/admin/schools',           colorScheme: 'forest',   enabled: true },
  { key: 'court-rentals',      label: 'Court Rentals',           desc: 'Rates, time blocks, and bookings',                       icon: 'SportsVolleyball',   path: '/admin/court-rentals',     colorScheme: 'darkBlue', enabled: true },
  { key: 'memberships',        label: 'Membership Tiers',        desc: 'Define member tiers, pricing, and hour allocations',     icon: 'CardMembership',     path: '/admin/memberships',       colorScheme: 'purple',   enabled: true },
  { key: 'documents',          label: 'Content',                 desc: 'Documents and email templates',                          icon: 'Description',        path: '/admin/documents',         colorScheme: 'olive',    enabled: true },
  { key: 'register-preview',   label: 'Preview Registration',    desc: 'Walk through new-user signup flow without saving data',  icon: 'Visibility',         path: '/register-preview',        colorScheme: 'gold',     enabled: true },
  { key: 'merge-athletes',     label: 'Merge Athletes',          desc: 'Combine duplicate athlete records into one',             icon: 'MergeType',          path: '/admin/merge-athletes',    colorScheme: 'red',      enabled: true },
  { key: 'backfill-parents',   label: 'Backfill Parents',        desc: 'One-time: populate parentUserIds on existing athletes',  icon: 'MergeType',          path: '/admin/backfill-parents',  colorScheme: 'olive',    enabled: true },
  { key: 'link-co-parents',    label: 'Link Co-Parents',         desc: 'Find and link accounts where auto-detection missed',     icon: 'Link',               path: '/admin/link-co-parents',   colorScheme: 'darkBlue', enabled: true },
  { key: 'migrate-p2',         label: 'Migrate p2 → guardian2',  desc: 'One-time: clean up legacy p2* fields on user docs',      icon: 'MergeType',          path: '/admin/migrate-p2',        colorScheme: 'olive',    enabled: true },
];
